import { useState, useEffect, useCallback } from 'react';
import { Download, Eye, Trash2 } from 'lucide-react';
import api from '../../services/api';

const statuses = ['new', 'reviewing', 'shortlisted', 'interview', 'offer', 'rejected', 'withdrawn'];

export default function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');

  const fetchApplications = useCallback(async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const res = await api.get(`/applications${params}`);
      setApplications(res.data.applications);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const updateStatus = async (id, status) => {
    await api.put(`/applications/${id}/status`, { status });
    fetchApplications();
    if (selectedApp?._id === id) setSelectedApp({ ...selectedApp, status });
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    await api.delete(`/applications/${id}`);
    fetchApplications();
    if (selectedApp?._id === id) setSelectedApp(null);
  };

  if (loading) return <div className="animate-pulse h-96 bg-gray-200 rounded-xl"></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Applications</h2>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600">
          <option value="">All Status</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700"><tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {applications.map((a) => (
              <tr key={a._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4">
                  <p className="font-medium text-gray-800 dark:text-white">{a.name}</p>
                  <p className="text-sm text-gray-500">{a.email}</p>
                </td>
                <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{a.jobId?.title || 'N/A'}</td>
                <td className="px-6 py-4">
                  <select value={a.status} onChange={(e) => updateStatus(a._id, e.target.value)} className={`px-2 py-1 text-xs rounded border ${a.status === 'new' ? 'bg-green-100' : a.status === 'rejected' ? 'bg-red-100' : 'bg-blue-100'}`}>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{new Date(a.createdAt).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => setSelectedApp(a)} className="p-2 text-gray-500 hover:text-cyan-600"><Eye size={16} /></button>
                  {a.resume && <a href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${a.resume}`} target="_blank" rel="noreferrer" className="p-2 text-gray-500 hover:text-cyan-600"><Download size={16} /></a>}
                  <button onClick={() => deleteApplication(a._id)} className="p-2 text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {applications.length === 0 && <div className="p-8 text-center text-gray-500">No applications found</div>}
      </div>

      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Application Details</h3>
            <div className="space-y-3">
              <div><label className="text-sm text-gray-500">Name</label><p className="font-medium">{selectedApp.name}</p></div>
              <div><label className="text-sm text-gray-500">Email</label><p>{selectedApp.email}</p></div>
              <div><label className="text-sm text-gray-500">Phone</label><p>{selectedApp.phone}</p></div>
              <div><label className="text-sm text-gray-500">Job</label><p>{selectedApp.jobId?.title || 'N/A'}</p></div>
              {selectedApp.coverLetter && <div><label className="text-sm text-gray-500">Cover Letter</label><p className="text-sm">{selectedApp.coverLetter}</p></div>}
              {selectedApp.linkedin && <div><label className="text-sm text-gray-500">LinkedIn</label><a href={selectedApp.linkedin} target="_blank" rel="noreferrer" className="text-cyan-600">{selectedApp.linkedin}</a></div>}
              {selectedApp.github && <div><label className="text-sm text-gray-500">GitHub</label><a href={selectedApp.github} target="_blank" rel="noreferrer" className="text-cyan-600">{selectedApp.github}</a></div>}
            </div>
            <button onClick={() => setSelectedApp(null)} className="mt-6 w-full py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}